/*  Shared  */
import { Notify, Response } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/*  React  */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const useFetchTagLinks = (params) => {
    const [tagLinks, setTagLinks] = useState([]);
    const [loading, setLoading] = useState(true);

    // calling refetchData will force a rerender on the hook
    const [shouldRefetchData, setShouldRefetch] = useState({});

    const refetchData = () => setShouldRefetch({});

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get('/tagLink', {
                    cancelToken: source.token,
                    params
                });
                if (!Response.hasData(response)) {
                    debug.w('No tag links found.', response);
                } else {
                    const data = Response.getData(response);

                    setTagLinks(data);
                }
            } catch (error) {
                debug.e('Error getting tag links.', error);
            }
            setLoading(false);
        };
        fetchData();

        return () => {
            // Cancel the request when the component unmounts
            source.cancel();
        };
    }, [shouldRefetchData, params]);

    return {
        loading,
        refetchData,
        tagLinks
    };
};

/**
 * A React hook to load tag links as needed to the API.
 * @returns {Object} An object containing the get function and a loading state.
 */
export const useFetchTagLinksAsync = () => {
    const [loading, setLoading] = useState(false);

    const getTagLinks = useCallback(async (params) => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        try {
            setLoading(true);
            const response = await axios.get('/tagLink', {
                cancelToken: source.token,
                params
            });
            if (!Response.hasData(response)) {
                debug.w('No tag links found.', response);
            }

            const data = Response.getData(response);

            setLoading(false);
            return data;
        } catch (err) {
            debug.e('Error getting tag links.', err);

            setLoading(false);
            return [];
        }
    }, []);

    return {
        getTagLinks,
        loading
    };
};

export const usePostTagLink = () => {
    const [saving, setSaving] = useState(false);
    const { t } = useTranslation();

    const postTagLink = useCallback(
        async ({
            applicationId,
            applyTag,
            name,
            description,
            color,
            platformTenantId,
            globalTagId,
            shouldNotify = true
        }) => {
            const params = {
                applicationId,
                applyTag,
                platformTenantId
            };

            if (globalTagId) {
                params.globalTagId = globalTagId;
            }
            if (!globalTagId && (name || color || description)) {
                params.globalTag = {
                    color,
                    description,
                    name
                };
            }

            try {
                if (!params.globalTagId && !params.globalTag) {
                    throw new Error('No globalTagId or globalTag provided');
                }

                setSaving(true);
                const response = await axios.post('/tagLink', params);
                if (!Response.hasData(response)) {
                    const msg = 'No TagLink Response';

                    throw new Error(msg);
                }

                if (shouldNotify) {
                    Notify.s(t('EditTagsModal.createTagLink.success'));
                }
                setSaving(false);

                return true;
            } catch (err) {
                debug.e('Unable to add TagLink', err);
                if (shouldNotify) {
                    Notify.e(t('EditTagsModal.createTagLink.error'));
                }
                setSaving(false);
                return false; // success = false
            }
        },
        [t]
    );

    return { postTagLink, saving };
};

export const usePutTagLink = () => {
    const [saving, setSaving] = useState(false);
    const { t } = useTranslation();

    const putTagLink = useCallback(
        async ({
            applyTag,
            platformTenantId,
            tagLinkId,
            shouldNotify = true
        }) => {
            const params = {
                applyTag,
                platformTenantId
            };

            try {
                if (!tagLinkId) {
                    throw new Error('No tagLinkId provided');
                }

                setSaving(true);
                const response = await axios.put(
                    `/tagLink/${tagLinkId}`,
                    params
                );

                if (!Response.hasData(response)) {
                    throw new Error('No TagLink Response');
                }

                if (shouldNotify) {
                    Notify.s(t('EditTagsModal.updateTagLink.success'));
                }
                setSaving(false);

                return true;
            } catch (err) {
                debug.e('Unable to save TagLink', err);
                if (shouldNotify) {
                    Notify.e(t('EditTagsModal.updateTagLink.error'));
                }
                setSaving(false);
                return false; // success = false
            }
        },
        [t]
    );

    return { putTagLink, saving };
};
